import { Link, Navigate, useParams } from 'react-router';
import { useI18n, type Lang } from '../i18n';
import { Plate } from '../ui/chrome';
import { LockGlyph } from '../ui/glyphs';
import { COPY, RESIDENTS, type Resident } from './features-content';

const birthday = (b: NonNullable<Resident['birthday']>, lang: Lang) =>
  lang === 'en' ? new Date(2000, b.month - 1, b.day).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : `${b.month} 月 ${b.day} 日`;

const sep = (lang: Lang) => (lang === 'en' ? ': ' : '：');

/** One resident's page: their own place, the full figure, the bio and the profile entries open from the start. */
export function ResidentPage() {
  const { id } = useParams();
  const { lang, tx } = useI18n();
  const at = RESIDENTS.findIndex((r) => r.id === id);
  const p = RESIDENTS[at];
  if (!p) return <Navigate to="/features/neighbours" replace />;
  const prev = RESIDENTS[(at + RESIDENTS.length - 1) % RESIDENTS.length]!;
  const next = RESIDENTS[(at + 1) % RESIDENTS.length]!;
  return (
    <div className="feature-page resident-page">
      <div className="wrap">
        <Link to="/features/neighbours" className="back-link">
          ← {tx(COPY.backToNeighbours)}
        </Link>
        <div className="resident-layout">
          <div className="resident-hero" style={{ backgroundImage: `url(/art/bg_${p.id}.webp)` }}>
            <img src={`/art/fig_${p.id}.webp`} alt={tx(p.name)} />
          </div>
          <div className="resident-info">
            <span className={`resident-tag${p.romance ? ' love' : ''}`}>{tx(p.romance ? COPY.romanceTag : COPY.localTag)}</span>
            <Plate as="h1" large>
              {tx(p.name)}
            </Plate>
            <p className="resident-meta">
              {tx(COPY.age).replace('{n}', String(p.age))}
              {p.birthday ? ` ・ ${tx(COPY.birthday)} ${birthday(p.birthday, lang)}` : ''}
            </p>
            <p className="role">{tx(p.role)}</p>
            <blockquote className="resident-quote">{tx(p.tagline)}</blockquote>
            <p className="resident-bio">{tx(p.bio)}</p>
            <section className="card dossier">
              <Plate>{tx(COPY.dossier)}</Plate>
              <dl>
                {p.entries.map((e) => (
                  <div key={e.label.en}>
                    <dt>{tx(e.label)}</dt>
                    <dd>{tx(e.text)}</dd>
                  </div>
                ))}
              </dl>
              {p.locked ? (
                <p className="dossier-locked">
                  <LockGlyph />
                  {tx(COPY.lockedNote).replace('{n}', String(p.locked)).replace('{name}', tx(p.name))}
                </p>
              ) : null}
            </section>
          </div>
        </div>
        <nav className="resident-nav">
          <Link to={`/neighbours/${prev.id}`}>
            ← {tx(COPY.prev)}
            {sep(lang)}
            {tx(prev.name)}
          </Link>
          <Link to={`/neighbours/${next.id}`}>
            {tx(COPY.nextResident)}
            {sep(lang)}
            {tx(next.name)} →
          </Link>
        </nav>
      </div>
    </div>
  );
}
